import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useCreateReason,
  usePriorityLevel,
  useUpdateReason,
} from "@features/cx/settings/hooks";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";

interface ReconnectionReason {
  id: number;
  configurationCode: string;
  name: string;
  longDescription: string;
  extraData: {
    acknowledgementTime: number;
    responseTime: number;
    resolutionTime: number;
    priorityLevel: number;
    requiredApproval: boolean;
    fees?: Array<{ fee_type: string; amount: number }>;
  };
}

interface ReconnectionReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: ReconnectionReason | null;
  onClose: () => void;
  onSave?: (reasonData: ReconnectionReason) => void;
}

export function ReconnectionReasonDialog({
  open,
  onOpenChange,
  reason,
  onClose,
  onSave,
}: ReconnectionReasonDialogProps) {
  const createReason = useCreateReason();
  const updateReason = useUpdateReason();
  const { data: priorityData } = usePriorityLevel();
  const remoteUtilityId = useRemoteUtilityId();
  const [formData, setFormData] = useState({
    code: reason?.configurationCode || "",
    name: reason?.name || "",
    longDescription: reason?.longDescription || "",
    acknowledgementTime: reason?.extraData.acknowledgementTime,
    responseTime: reason?.extraData.responseTime,
    resolutionTime: reason?.extraData.resolutionTime,
    priority: reason?.extraData.priorityLevel,
    requiresApproval: reason?.extraData.requiredApproval,
    reconnection_fees: (reason?.extraData.fees || []).map((fee: any) => ({
      fee_type: fee.feeType ?? fee.fee_type ?? "",
      amount: fee.amount ?? 0,
    })),
  });
  const currency = useCurrency();

  const getPriorityOptions = useMemo(() => {
    if (!priorityData || !Array.isArray(priorityData)) {
      return [];
    }
    return priorityData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [priorityData]);

  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Reason Name",
      placeholder: "e.g., Non-payment",
      required: true,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Provide a clear and descriptive name for the reconnection reason (e.g., “Bill Paid,” “Issue Resolved”). This will be shown to users when selecting a reconnection reason.",
    },
    {
      name: "code",
      type: "text",
      label: "Reason Code",
      placeholder: "e.g., NON_PAY",
      required: true,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        " Enter a unique code for the reconnection reason. This code helps with system mapping, reporting, and integration workflows.",
    },
    {
      name: "longDescription",
      type: "textarea",
      label: "Description",
      placeholder: "Describe when this reason should be used...",
      rows: 3,
      className: "col-span-2",
      showHelperTooltip: true,
      tooltipText:
        "Provide a detailed description of this reconnection reason. Include any important information users should know, such as supported card types, processing times, or special requirements.",
    },
    {
      name: "acknowledgementTime",
      type: "number",
      label: "Acknowledgement Time (Hours)",
      placeholder: "24",
      min: 1,
      required: true,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Maximum time allowed to acknowledge receipt of the complaint and send initial response to the customer. This is the first customer touchpoint.",
    },
    {
      name: "responseTime",
      type: "number",
      label: "Response Time (Hours)",
      placeholder: "48",
      min: 1,
      required: true,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Maximum time allowed to provide a substantive response or action plan to the customer after initial acknowledgment.",
    },
    {
      name: "resolutionTime",
      type: "number",
      label: "Resolution Time (Hours)",
      placeholder: "72",
      min: 1,
      required: true,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Maximum time allowed to fully resolve the complaint, including all necessary actions, approvals, and customer closure confirmation.",
    },
    {
      name: "priority",
      type: "select",
      label: "Priority",
      placeholder: "Select priority level",
      required: true,
      options: getPriorityOptions,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Severity level determines priority calculation, response times, and escalation procedures. Critical issues get immediate attention, while low severity issues follow standard processing.",
    },
    {
      name: "requiresApproval",
      type: "switch",
      label: "Requires Approval",
      helperText: "Whether this reason requires manager approval",
      className: "col-span-2",
    },
    {
      name: "visibleOnSelfServicePortal",
      type: "switch",
      label: "Visible on Self Service Portal",
      tooltipText:
        "When enabled, customers can see and request this reconnection directly from the self service portal.",
      helperText:
        "Allow customers to view and initiate a reconnection request from the self-service portal",
      className: "col-span-2",
      showHelperTooltip: true,
    },
    {
      name: "reconnection_fees",
      type: "form-array",
      label: "Reconnection Fees",
      addButtonText: "Add Fee",
      minItems: 0,
      className: "col-span-2",
      itemFields: [
        {
          name: "fee_type",
          type: "text",
          label: "Fee Type",
          placeholder: "Enter fee type",
          required: true,
          className: "col-span-1",
        },
        {
          name: "amount",
          type: "number",
          label: `Amount (${currency})`,
          placeholder: "0.00",
          required: true,
          min: 0,
          className: "col-span-1",
        },
      ],
    },
    {
      name: "total_fees",
      type: "custom",
      customRender: (field, form) => {
        const fees = form.watch("reconnection_fees") || [];
        const total = fees.reduce(
          (acc: number, fee: any) => acc + (parseFloat(fee.amount) || 0),
          0,
        );
        return (
          <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <span className="text-sm font-semibold text-blue-900">
              Total Fees:
            </span>
            <span className="text-sm font-bold text-blue-900">
              {currency}
              {total.toFixed(2)}
            </span>
          </div>
        );
      },
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    if (open) {
      const initialData = {
        code: reason?.configurationCode || "",
        name: reason?.name || "",
        longDescription: reason?.longDescription || "",
        acknowledgementTime: reason?.extraData?.acknowledgementTime,
        responseTime: reason?.extraData?.responseTime,
        resolutionTime: reason?.extraData?.resolutionTime,
        priority: reason?.extraData?.priorityLevel,
        requiresApproval: reason?.extraData?.requiredApproval,
        visibleOnSelfServicePortal:
          reason?.extraData?.is_visible_on_self_portal === 1 ||
          reason?.extraData?.is_visible_on_self_portal === true ||
          reason?.extraData?.visibleOnSelfServicePortal ||
          false,
        reconnection_fees: (reason?.extraData?.fees || []).map((fee: any) => ({
          fee_type: fee.feeType ?? fee.fee_type ?? "",
          amount: fee.amount ?? 0,
        })),
      };
      setFormData(initialData);
      form.reset(initialData);
    }
  }, [reason, open, form]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = (data: any) => {
    const operation = reason ? "update" : "create";
    switch (operation) {
      case "update":
        const payloadEdit = {
          id: reason?.id,
          name: data.name,
          configuration_code: data.code,
          long_description: data.longDescription,
          request_type: "Reconnection",
          remote_utility_id: Number(remoteUtilityId),
          extra_data: {
            priority_level: String(data.priority),
            acknowledgement_time: Number(data.acknowledgementTime),
            response_time: Number(data.responseTime),
            resolution_time: Number(data.resolutionTime),
            required_approval: data.requiresApproval ?? false,
            is_visible_on_self_portal: data.visibleOnSelfServicePortal ? 1 : 0,
            fees: (data.reconnection_fees || []).map((fee: any) => ({
              fee_type: fee.fee_type,
              amount: Number(fee.amount),
            })),
          },
        };

        updateReason.mutateAsync(payloadEdit);
        break;

      case "create":
        const payloadAdd = {
          name: data.name,
          configuration_code: data.code,
          long_description: data.longDescription,
          request_type: "Reconnection",
          remote_utility_id: Number(remoteUtilityId),
          extra_data: {
            priority_level: String(data.priority),
            acknowledgement_time: Number(data.acknowledgementTime),
            response_time: Number(data.responseTime),
            resolution_time: Number(data.resolutionTime),
            required_approval: data.requiresApproval ?? false,
            is_visible_on_self_portal: data.visibleOnSelfServicePortal ? 1 : 0,
            fees: (data.reconnection_fees || []).map((fee: any) => ({
              fee_type: fee.fee_type,
              amount: Number(fee.amount),
            })),
          },
        };

        createReason.mutateAsync(payloadAdd);
        break;
    }

    handleClose();
  };
  // Handle form reset
  const handleReset = () => {
    const resetData = {
      code: "",
      name: "",
      longDescription: "",
      acknowledgementTime: "",
      responseTime: "",
      resolutionTime: "",
      priority: "",
      requiresApproval: false,
      reconnection_fees: [],
    };
    setFormData(resetData);
    form.reset(resetData);
  };

  // Handle dialog close
  const handleClose = () => {
    handleReset();
    onClose();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {reason
              ? "Edit Disconnection Reason"
              : "Add New Reconnection Reason"}
          </DialogTitle>
          <DialogDescription>
            Configure the reason, timing requirements, and priority settings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            onFieldChange={handleFieldChange}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: reason ? "Update Reason" : "Add Reason",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
